import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type BookingState = 'none' | 'pending' | 'booked';

export interface MatchBookingStatus {
  status: BookingState;
  /** @deprecated use status instead */
  isBooked: boolean;
  bookingChannel: 'playtomic' | 'whatsapp' | null;
  clubName: string | null;
  bookedBy: string | null;
  bookedByName: string | null;
  bookedAt: string | null;
  courtName: string | null;
}

export const useMatchBookingStatus = (matchId: string | null | undefined) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['match-booking-status', matchId],
    queryFn: async (): Promise<MatchBookingStatus> => {
      // Fetch the most recent booking_feedback row for this match (pending or booked)
      const { data, error } = await supabase
        .from('booking_feedback')
        .select('booking_channel, club_name, user_id, created_at, booked, court_name_or_number')
        .eq('match_id', matchId!)
        .or('booked.is.null,booked.eq.true')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return { status: 'none', isBooked: false, bookingChannel: null, clubName: null, bookedBy: null, bookedByName: null, bookedAt: null, courtName: null };
      }

      // Fetch the user's name
      let bookedByName: string | null = null;
      if (data.user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', data.user_id)
          .maybeSingle();
        bookedByName = profile?.full_name?.split(' ')[0] || null;
      }

      const isPending = data.booked === null;

      return {
        status: isPending ? 'pending' : 'booked',
        isBooked: !isPending,
        bookingChannel: data.booking_channel as 'playtomic' | 'whatsapp',
        clubName: data.club_name,
        bookedBy: data.user_id,
        bookedByName,
        bookedAt: data.created_at,
        courtName: (data as any).court_name_or_number || null,
      };
    },
    enabled: !!matchId && !!user,
    staleTime: 2 * 60 * 1000,
  });
};
